import React from 'react';
import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSelector } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import type { ICampaign } from '../../../redux/buzzbridge/types';
import type { RootState } from '../../../redux/root-reducer';
import { DEFAULT_INFLUENCER_TIERS } from '../../../constants/ugcCampaignConfigDefaults';
import type { IInfluencerTierConfig } from '../../../types/global/ugcCampaignConfig.types';
import { getUgcCampaignFilledCount, getUgcCampaignMaxSlots } from '../../../utils/ugcCampaignCapacity';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_SURFACE_CONTAINER_LOW,
    BRAND_SURFACE_CONTAINER_HIGHEST,
} from '../brandDashboardTokens';

function formatCurrency(amount?: number) {
    if (!amount) return '$0';
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatDate(iso?: string) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function daysRemaining(endDate?: string) {
    if (!endDate) return null;
    const diff = Math.ceil((new Date(endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? `${diff} days left` : 'Ended';
}

const REWARD_LABELS: Record<string, string> = {
    paid: 'Paid',
    gifted: 'Product Only',
    both: 'Paid + Product',
};

export interface OverviewCardProps {
    icon: React.ReactNode;
    label: string;
    value: string;
    sub?: string;
    accent?: string;
    badge?: React.ReactNode;
    /** Extra content below sub (e.g. chip groups). */
    footer?: React.ReactNode;
    /** Override primary value typography (long addresses, etc.). */
    valueSx?: SxProps<Theme>;
    /** Merge into outer card `sx` (e.g. fixed width for audition overview row). */
    cardSx?: SxProps<Theme>;
}

export function OverviewCard({
    icon,
    label,
    value,
    sub,
    accent = '#fff',
    badge,
    footer,
    valueSx,
    cardSx,
}: OverviewCardProps) {
    return (
        <Box
            sx={{
                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                p: 2.5,
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                minWidth: 0,
                position: 'relative',
                ...cardSx,
            }}>
            {badge && <Box sx={{ position: 'absolute', top: 12, right: 12 }}>{badge}</Box>}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1.5,
                        bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: BRAND_LUXE_PRIMARY,
                        flexShrink: 0,
                    }}>
                    {icon}
                </Box>
                <Typography
                    sx={{
                        fontSize: 10,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        textTransform: 'uppercase',
                        letterSpacing: '0.14em',
                        color: 'rgba(255,255,255,0.5)',
                    }}>
                    {label}
                </Typography>
            </Box>
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: 22,
                    color: accent,
                    letterSpacing: '-0.02em',
                    ...valueSx,
                }}>
                {value}
            </Typography>
            {sub && (
                <Typography sx={{ fontSize: 12, fontFamily: FONT_ENUM.MAIN_MEDIUM, color: 'rgba(255,255,255,0.4)' }}>
                    {sub}
                </Typography>
            )}
            {footer ? <Box sx={{ minWidth: 0 }}>{footer}</Box> : null}
        </Box>
    );
}

export function CampaignOverviewCards({ campaign }: { campaign: ICampaign }) {
    const configTiers = useSelector((state: RootState) => state.configuration.influencerTiers);
    const tiers = configTiers?.length ? configTiers : DEFAULT_INFLUENCER_TIERS;
    const tierLabels: Record<string, string> = Object.fromEntries(
        tiers.map((t: IInfluencerTierConfig) => [t.id, `${t.name} (${t.reach})`]),
    );

    const remaining = daysRemaining(campaign.end_date);
    const isAppInstall = Boolean((campaign as any).app_install?.enabled);
    const appInstallBaseCents = isAppInstall ? Math.round(Number((campaign as any).base_payment_per_creator_cents) || 0) : 0;
    const appInstallSeats = isAppInstall ? Math.max(1, Number(campaign.total_participants_count) || 1) : 0;
    const displayTotalBudget = isAppInstall && appInstallBaseCents > 0
        ? (campaign.total_budget || 0) + (appInstallBaseCents * appInstallSeats) / 100
        : (campaign.total_budget || 0);
    const spent = (campaign.total_budget || 0) - (campaign.available_budget || 0);
    const spentPct = campaign.total_budget ? Math.round((spent / campaign.total_budget) * 100) : 0;
    const isFunded = campaign.funded === true;
    const filledCreators = getUgcCampaignFilledCount(campaign);
    const requestedCreators = getUgcCampaignMaxSlots(campaign);
    const participantsValue =
        requestedCreators != null ? `${filledCreators}/${requestedCreators}` : `${filledCreators}`;

    return (
        <Box
            id="campaign-overview-cards"
            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
            <OverviewCard
                icon={<AttachMoneyIcon sx={{ fontSize: 20 }} />}
                label="Total Budget"
                value={formatCurrency(displayTotalBudget)}
                sub={isAppInstall && appInstallBaseCents > 0
                    ? `$${campaign.total_budget?.toLocaleString('en-US') ?? 0} CPA · $${((appInstallBaseCents * appInstallSeats) / 100).toLocaleString('en-US')} base pay`
                    : `${formatCurrency(campaign.available_budget)} available · ${spentPct}% spent`}
                accent={BRAND_LUXE_PRIMARY}
                badge={
                    isFunded ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CheckCircleIcon sx={{ fontSize: 14, color: BRAND_LUXE_SECONDARY }} />
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 10,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.14em',
                                    color: BRAND_LUXE_SECONDARY,
                                    textShadow: '0 0 8px rgba(0,253,193,0.4)',
                                }}>
                                Funded
                            </Typography>
                        </Box>
                    ) : undefined
                }
            />
            <OverviewCard
                icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 18 }} />}
                label="Timeline"
                value={`${formatDate(campaign.start_date)} – ${formatDate(campaign.end_date)}`}
                sub={remaining ?? undefined}
            />
            <OverviewCard
                icon={<GroupOutlinedIcon sx={{ fontSize: 20 }} />}
                label="Participants"
                value={participantsValue}
                sub={tierLabels[campaign.influencer_tier || ''] || campaign.influencer_tier}
            />
            <OverviewCard
                icon={<WorkspacePremiumOutlinedIcon sx={{ fontSize: 20 }} />}
                label="Reward"
                value={REWARD_LABELS[campaign.reward || ''] || campaign.reward || '—'}
                sub={[
                    campaign.platforms?.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(', '),
                    campaign.exclusive_rights ? 'Exclusive rights · 12 mo' : null,
                ]
                    .filter(Boolean)
                    .join(' · ')}
            />
        </Box>
    );
}
